import React, { useCallback } from "react";
import { clinicianPortalApi } from "../api/clinicianPortalApi";
import { ClientForm } from "../components/ClientForm";
import { ErrorState, LoadingState } from "../components/FeedbackStates";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import { clientProfilePath } from "../config/navigation";
import { usePortalData } from "../hooks/usePortalData";
import { usePortalMutation } from "../hooks/usePortalMutation";
import {
  navigateWithinPortal,
  withClinicianCode,
} from "../routing/portalRoutes";

export function EditClientPage({
  clinicianCode,
  patientId,
}: {
  clinicianCode: string;
  patientId: string;
}) {
  const request = useCallback(
    async (signal: AbortSignal) => {
      const [client, formConfig, relationOptions] = await Promise.all([
        clinicianPortalApi.getClient(clinicianCode, patientId, false, signal),
        clinicianPortalApi.getRecordFormConfig(clinicianCode, signal),
        clinicianPortalApi.getRelationOptions(clinicianCode, signal),
      ]);
      return { client, formConfig, relationOptions };
    },
    [clinicianCode, patientId],
  );
  const state = usePortalData(request);
  const mutation = usePortalMutation();
  const data =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const available = Boolean(
    data?.client.found &&
      data.client.authorized &&
      data.client.therapist &&
      data.client.patient &&
      data.formConfig.found &&
      data.formConfig.authorized &&
      data.formConfig.therapist &&
      data.relationOptions.found &&
      data.relationOptions.authorized &&
      data.relationOptions.therapist,
  );
  const patient = available ? data?.client.patient : null;
  const profilePath = clientProfilePath(patientId);

  return (
    <ClinicianPortalLayout
      activeGlobalKey="clients"
      clinicianCode={clinicianCode}
      therapistRole={data?.client.therapist?.role}
    >
      <PortalHeader
        title={patient ? `Edit Client: ${patient.nickname}` : "Edit Client"}
        clinicianName={
          available ? data?.client.therapist?.therapistName : undefined
        }
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={[
              { label: "My Clients", path: "/clinician-portal/clients" },
              {
                label: patient?.nickname || "Client Profile",
                path: profilePath,
              },
              { label: "Edit Client" },
            ]}
          />
        }
      />
      <div className="cp-page-content cp-page-content--profile">
        {state.status === "loading" ? (
          <LoadingState label="Loading Client form" />
        ) : null}
        {state.status === "error" ? (
          <ErrorState error={state.error} onRetry={state.refresh} />
        ) : null}
        {data && !available ? (
          <ErrorState error={new Error("Client editing is unavailable")} />
        ) : null}
        {available && data && patient ? (
          <ClientForm
            key={patient.id}
            mode="edit"
            fields={data.formConfig.fields}
            relationOptions={data.relationOptions.options}
            patient={patient}
            clinicianCode={clinicianCode}
            cancelPath={profilePath}
            isSubmitting={mutation.state.status === "pending"}
            submissionError={
              mutation.state.status === "error"
                ? mutation.state.error
                : undefined
            }
            clearSubmissionError={mutation.reset}
            onSubmit={async (values) => {
              const result = await mutation.run("update-client", (signal) =>
                clinicianPortalApi.updateClient(
                  clinicianCode,
                  patientId,
                  values,
                  signal,
                ),
              );
              if (result?.updated && result.patient) {
                navigateWithinPortal(
                  withClinicianCode(
                    clientProfilePath(result.patient.id),
                    clinicianCode,
                  ),
                );
              }
            }}
          />
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
